import { Col, Input, Row, Spin } from "antd";
import queryClient from "api/queryClient";
import { useGetRegistrationStructureQuery } from "api/TenantSlug/UserStructure/hooks.ts";
import { useRegisterUserMutation } from "api/User/hooks.ts";
import { AxiosError } from "axios";
import {
    buildRegistrationFormDefaults,
    buildRegistrationFormSchema,
} from "components/Forms/TenantRegistrationFields/registrationFormSchema.ts";
import { TenantRegistrationFields } from "components/Forms/TenantRegistrationFields/TenantRegistrationFields.tsx";
import { TenantRegistrationFormData } from "components/Forms/TenantRegistrationFields/types.ts";
import {
    getActiveRegistrationFields,
    getTenantRegistrationStatus,
    getVisibleRegistrationFields,
    pickRegistrationFieldValues,
} from "components/Forms/TenantRegistrationFlow/registrationFlow.ts";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { AuthLink } from "components/UI/AuthLink/AuthLink.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess";
import { TenantAuthFormWrapper } from "components/Wrappers/TenantAuthFormWrapper/TenantAuthFormWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { Trans } from "react-i18next";
import { CheckEmailStatus, RegistrationPasswordType, RoleEnum } from "types/enums.ts";
import { ApiErrorResponse } from "types/models.ts";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

import { useTenantCheckEmail } from "./useTenantCheckEmail";

import TenantData = App.Data.TenantData;
import FormFieldData = App.Data.FormFieldData;

// Intentionally mirrors AffiliateRegistrationForm — same flow (see docs/auth-flow/advertiser-tenant-registration.md),
// duplicated on purpose rather than shared, only the role differs. Keep both in sync when this flow changes.

const EMAIL_CHECK_DEBOUNCE_MS = 600;
const EMPTY_FORM_FIELDS: FormFieldData[] = [];
const EMPTY_FORM_FIELD_IDS: string[] = [];

interface AdvertiserRegistrationFormContentProps {
    tenant: TenantData;
    formFields?: FormFieldData[];
    isSubmitting?: boolean;
    submitError?: string[] | null;
    submitApiError?: AxiosError<ApiErrorResponse> | null;
    onEmailStatusChange?: (status: CheckEmailStatus | "idle", excludedFormFieldIds: string[]) => void;
    onAutoSubmit?: (data: TenantRegistrationFormData) => void;
}

export const AdvertiserRegistrationFormContent: FC<AdvertiserRegistrationFormContentProps> = ({
    tenant,
    formFields = EMPTY_FORM_FIELDS,
    isSubmitting,
    submitError,
    submitApiError,
    onEmailStatusChange,
    onAutoSubmit,
}) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCommon } = usePaldockTranslation("common");
    const { t: tRegistration } = usePaldockTranslation("forms.advertiser-registration");
    const { control, fieldNames, formState, getValues, handleErrorsFromErrorResponse, setValue, trigger } =
        useGeneralForm<TenantRegistrationFormData>();
    const {
        status,
        nickname,
        excludedFormFieldIds = EMPTY_FORM_FIELD_IDS,
        isCheckingEmail,
        checkEmail,
        resetStatus,
    } = useTenantCheckEmail(tenant.id);
    const emailValue = useWatch({ control, name: fieldNames.email.name });
    const isEmailValid = Boolean(emailValue) && !formState.errors.email;
    const activeFormFields = useMemo(
        () => getActiveRegistrationFields(formFields, excludedFormFieldIds),
        [excludedFormFieldIds, formFields]
    );
    const visibleFormFields = getVisibleRegistrationFields(activeFormFields);
    const autoSubmittedEmailRef = useRef<string | null>(null);
    const resetEmailDependentState = () => {
        resetStatus();
        setValue("fields", buildRegistrationFormDefaults(formFields).fields);
        autoSubmittedEmailRef.current = null;
    };

    useEffect(() => {
        if (!isEmailValid) {
            return;
        }
        const timer = setTimeout(() => checkEmail(emailValue, RoleEnum.Advertiser), EMAIL_CHECK_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [emailValue, isEmailValid]);

    useEffect(() => {
        if (nickname) {
            setValue(fieldNames.nickname.name, nickname);
        }
    }, [nickname]);

    useEffect(() => {
        onEmailStatusChange?.(status, excludedFormFieldIds);
    }, [status, excludedFormFieldIds]);

    useEffect(() => {
        if (submitApiError) handleErrorsFromErrorResponse(submitApiError);
    }, [submitApiError]);

    const {
        isEligibleExistingAccount: isExistingUser,
        isSameRole,
        isPendingRole,
    } = getTenantRegistrationStatus(status);

    useEffect(() => {
        if (
            !isExistingUser ||
            !onAutoSubmit ||
            visibleFormFields.length > 0 ||
            !nickname ||
            !emailValue ||
            isSubmitting ||
            autoSubmittedEmailRef.current === emailValue
        ) {
            return;
        }

        autoSubmittedEmailRef.current = emailValue;
        void trigger().then((isValid) => {
            if (isValid) onAutoSubmit?.(getValues());
        });
    }, [emailValue, isExistingUser, isSubmitting, nickname, visibleFormFields.length]);

    return (
        <TenantAuthFormWrapper
            buttonText={tCommon("register")}
            loading={isSubmitting}
            buttonDisabled={isSameRole || isPendingRole || status === "idle" || isSubmitting}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.email.name}
                        label={tFields("email.label")}
                        required
                        className="!mb-0"
                    >
                        <Input
                            placeholder={tFields("email.placeholder")}
                            status={isSameRole ? "error" : undefined}
                            suffix={isCheckingEmail ? <Spin size="small" /> : undefined}
                            onChange={resetEmailDependentState}
                            onBlur={() => isEmailValid && checkEmail(emailValue, RoleEnum.Advertiser)}
                        />
                    </FormItem>
                </Col>
                {isSameRole && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                <Trans
                                    i18nKey="forms.advertiser-registration.messages.already_member"
                                    components={{
                                        a: (
                                            <AuthLink
                                                to="/$tenantSlug/auth/login"
                                                params={{ tenantSlug: tenant.slug_name }}
                                            />
                                        ),
                                    }}
                                />
                            }
                        />
                    </Col>
                )}
                {isPendingRole && (
                    <Col span={24}>
                        <AuthAlert type="warning" message={tRegistration("messages.pending_role")} />
                    </Col>
                )}
                {status === CheckEmailStatus.New && (
                    <Col span={24}>
                        <AuthAlert type="info" message={tRegistration("messages.new_user_info")} />
                    </Col>
                )}
                {status === CheckEmailStatus.ExistingNoAccess && (
                    <Col span={24}>
                        <AuthAlert type="warning" message={tRegistration("messages.existing_no_access_info")} />
                    </Col>
                )}
                {(status === CheckEmailStatus.New || isExistingUser) && (
                    <Col span={24}>
                        <FormItem
                            control={control}
                            name={fieldNames.nickname.name}
                            label={tFields("nickname.label")}
                            required
                            className="!mb-0"
                        >
                            <Input placeholder={tFields("nickname.placeholder")} disabled={isExistingUser} />
                        </FormItem>
                    </Col>
                )}
                {(status === CheckEmailStatus.New || isExistingUser) && activeFormFields.length > 0 && (
                    <Col span={24}>
                        <TenantRegistrationFields fields={activeFormFields} />
                    </Col>
                )}
                {submitError && submitError.length > 0 && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                submitError.length === 1 ? (
                                    submitError[0]
                                ) : (
                                    <ul className="list-disc pl-4">
                                        {submitError.map((message) => (
                                            <li key={message}>{message}</li>
                                        ))}
                                    </ul>
                                )
                            }
                        />
                    </Col>
                )}
            </Row>
        </TenantAuthFormWrapper>
    );
};

interface AdvertiserRegistrationFormProps {
    tenant: TenantData;
}

export const AdvertiserRegistrationForm: FC<AdvertiserRegistrationFormProps> = ({ tenant }) => {
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string[] | null>(null);
    const { t } = usePaldockTranslation("forms.registration");
    const { t: tAuth } = usePaldockTranslation("auth.registration");
    const { t: tCommon } = usePaldockTranslation("common");
    // Only Content knows the checked email's status; onEmailStatusChange feeds it back here so the
    // success screen can tell an existing Paldock user (RegistrationRequestConfirmationMail — confirm
    // joining this workspace) apart from a brand new one (UserActivationMail — activate the account).
    const isExistingUserRef = useRef(false);
    const [excludedFormFieldIds, setExcludedFormFieldIds] = useState<string[]>([]);
    const registrationStructureQuery = useGetRegistrationStructureQuery("registration-advertiser");
    const formFields = useMemo(
        () =>
            [...(registrationStructureQuery.data?.formStructure.formFields ?? [])].sort((a, b) => a.weight - b.weight),
        [registrationStructureQuery.data]
    );
    const activeFormFields = useMemo(
        () => getActiveRegistrationFields(formFields, excludedFormFieldIds),
        [excludedFormFieldIds, formFields]
    );
    const schema = useMemo(() => buildRegistrationFormSchema(activeFormFields), [activeFormFields]);
    const defaultValues = useMemo(() => buildRegistrationFormDefaults(formFields), [formFields]);

    const registerUserMutation = useRegisterUserMutation({
        onSuccess: () => {
            setSubmitError(null);
            setRegistrationSuccess(true);
            queryClient.invalidateQueries({ queryKey: ["me"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
        onError: (error) => {
            setSubmitError(extractValidationErrorMessages(error));
        },
    });

    const onSubmit = (data: TenantRegistrationFormData) => {
        setSubmitError(null);
        registerUserMutation.mutate({
            email: data.email,
            nickname: data.nickname,
            type: RegistrationPasswordType.WithoutPassword,
            return_url: `${import.meta.env.VITE_APP_URL}/${tenant.slug_name}/auth/activate/{id}/{hash}`,
            role: RoleEnum.Advertiser,
            tenant_id: tenant.id,
            fields: pickRegistrationFieldValues(activeFormFields, data.fields),
        });
    };

    if (registrationSuccess) {
        return (
            <RegistrationSuccess
                message={
                    isExistingUserRef.current
                        ? t("messages.existing_user_registration_success_message")
                        : t("messages.registration_success_message")
                }
            />
        );
    }

    if (registrationStructureQuery.isLoading) {
        return <Spin className="flex justify-center" />;
    }

    if (registrationStructureQuery.isError) {
        return <AuthAlert type="error" message={t("messages.registration_form_unavailable")} />;
    }

    return (
        <>
            <GeneralFormProvider<TenantRegistrationFormData>
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                requiredMark={false}
                resetOnSubmit={false}
            >
                <AdvertiserRegistrationFormContent
                    tenant={tenant}
                    formFields={formFields}
                    isSubmitting={registerUserMutation.isPending}
                    submitError={submitError}
                    submitApiError={registerUserMutation.error}
                    onAutoSubmit={onSubmit}
                    onEmailStatusChange={(status, excludedIds) => {
                        setExcludedFormFieldIds(excludedIds);
                        isExistingUserRef.current = getTenantRegistrationStatus(status).isEligibleExistingAccount;
                    }}
                />
            </GeneralFormProvider>
            <div className="flex justify-center mt-4 text-md text-black">
                {tAuth("messages.already_have_account")}{" "}
                <AuthLink to="/$tenantSlug/auth/login" params={{ tenantSlug: tenant.slug_name }} className="ml-1">
                    {tCommon("login")}
                </AuthLink>
            </div>
        </>
    );
};
